'use client';

import {
  useCallback,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  type ReactNode,
} from 'react';
import {
  useRenderSettingsActions,
  useRenderSettingsState,
  useSectionChanges,
} from '../../model/render-form-store.selectors';
import { useRenderStore } from '../../model/render-form.store';
import { isEmptyRefine } from '../../lib/render-settings-storage';
import { useRenderSettings } from '../../hooks/use-render-settings';
import { useRenderForm } from '../../hooks/use-render-form';
import { useAngleRendersData } from '../../hooks/use-angle-renders-data';
import { RenderModeToggle } from '../components/RenderModeToggle';
import { VariantNudgeCallout } from '../components/VariantNudgeCallout';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import { useTranslations } from 'next-intl';
import { OutputStyleCarousel } from './OutputStyleCarousel';
import { StyleSection } from './StyleSection';
import { EnvironmentSection } from './EnvironmentSection';
import { MaterialsSection } from './MaterialsSection';
import { ColorsSection } from './ColorsSection';
import { StructureSection } from './StructureSection';
import { PromptsSection } from './PromptsSection';
import { ModelSection } from './ModelSection';
import { EffortSection } from './EffortSection';
import { HiResSection } from './HiResSection';
import { ExtendedThinkingSection } from './ExtendedThinkingSection';
import { ProjectGoalsSection } from './ProjectGoalsSection';
import type {
  RenderModelFamily,
  RenderEffort,
  RenderMode,
} from '@/types/render';
import {
  EnvironmentSettingEnum,
  type EnvironmentSetting,
  generateSeed,
} from '@/shared/constants';
import type { RenderCreateRequest } from '@/types/render';
import type {
  Control,
  Angle,
  VariantNudge,
  ReferenceNudge,
} from '@/types/concept';
import { ScrollArea } from '@/shared/ui/shadcn/scroll-area';
import { SettingsCard, SettingsRowGroup } from '@/shared/ui';
import { useConceptStore } from '@/features/design/concept/model/concept-editor-store';
import { useProject } from '@/features/project/hooks/use-projects';
import {
  PackUsageNotice,
  useProjectFeatures,
} from '@/features/subscription';

interface ScrollableRenderSettingsProps {
  variant?: 'main' | 'derivative';
  onSubmit: (data: RenderCreateRequest) => Promise<void>;
  defaultValues?: Partial<RenderCreateRequest['data']['attributes']>;
  controls?: Control[];
  angle?: Angle | null;
  conceptId?: string;
  conceptAngleId?: string;
  projectId?: string;
  onControlsUpdate?: (controls: Control[]) => void;
  /** Server-driven "try a new variant" nudge (concept `meta.variant_nudge`).
   *  When non-null, a tappable callout is shown above the Explore/Refine toggle
   *  that switches the next render to Explore. Null → no callout. */
  variantNudge?: VariantNudge | null;
  /** Server-driven "save as reference to steer your designs" nudge (concept
   *  `meta.reference_nudge`). When non-null, an info banner is shown at the top
   *  of the main-angle settings teaching the user they can save a design as a
   *  reference. Null → no banner. */
  referenceNudge?: ReferenceNudge | null;
  onNavigateToReferences?: () => void;
  /** A refine render is in flight for this angle → render the settings as a
   *  READ-ONLY readout of what's rendering: seed from the in-flight render's own
   *  settings (via `defaultValues`), force the Refine pill, lock + dim every row.
   *  Mirrors Apple `RenderSettingsContent.isReadOnly` / Android. */
  isReadOnly?: boolean;
  /** Parent render (image being refined) image + id — points the Explore/Refine
   *  toggle at the parent so the Refine pill shows while read-only (the in-flight
   *  render has no completed image of its own yet). */
  readOnlyParentRenderUrl?: string | null;
  readOnlyParentRenderId?: string | null;
  /** Bottom padding for the scroll content. `lg` reserves room for a
   *  floating CTA overlay above the scroll area (inline two-column panel).
   *  Defaults to `sm` for the regular tight layout. */
  bottomScrollPadding?: 'sm' | 'lg';
  /** Stacked (single-column) layout: render the settings in normal page flow
   *  — no internal `ScrollArea` / fixed height — so the angle image above and
   *  these settings share ONE page scroll (Apple/Android one-scroll parity).
   *  The Generate button floats above the bottom nav (owned by the caller). */
  stacked?: boolean;
}

export interface ScrollableRenderSettingsRef {
  submitRender: () => void;
}

export const ScrollableRenderSettings = forwardRef<
  ScrollableRenderSettingsRef,
  ScrollableRenderSettingsProps
>(
  (
    {
      variant = 'main',
      onSubmit,
      defaultValues = {},
      controls: initialControls = [],
      angle = null,
      conceptId,
      conceptAngleId,
      projectId,
      onControlsUpdate,
      variantNudge = null,
      referenceNudge = null,
      onNavigateToReferences,
      isReadOnly = false,
      readOnlyParentRenderUrl = null,
      readOnlyParentRenderId = null,
      bottomScrollPadding = 'sm',
      stacked = false,
    },
    ref
  ) => {
    const isDerivative = variant === 'derivative';
    const controls = initialControls;
    const tInherited = useTranslations('render.settings.inherited');
    const tRefine = useTranslations('render.refineMode');

    // Manual engine knobs (Effort, Model, Extended thinking, Hi-Res) are shown
    // ONLY to advanced-control holders (Builder Pass, grandfathered
    // Pro/Designer). Free + one-time Facade Pack buyers don't see them at all —
    // the rows are absent and the render request omits those keys so the server
    // auto-resolves effort. Output style stays visible for everyone. Mirrors
    // Apple/Android. The omission of the four keys happens in `useRenderSubmit`,
    // which reads the same per-building flag.
    const hasAdvancedRenderControls =
      useProjectFeatures(projectId).has_advanced_render_controls;

    /// Lifted so the Project goals row in the refine card can open the
    /// embedded goals editor (mirrors Apple's NavigationStack push and
    /// Android's PROJECT_GOALS panel switch — no navigation away from the
    /// sheet).
    const [goalsSheetOpen, setGoalsSheetOpen] = useState(false);

    // Live project goals — drives the brief group's "fill in goals first"
    // hint on Explore main. Mirrors Apple, where the brief gate reads from
    // `hasProjectGoals` on the cached project.
    const { data: projectResponse } = useProject(projectId ?? '', {
      enabled: !!projectId,
    });
    const projectGoals = projectResponse?.data?.attributes?.goals ?? '';
    const hasProjectGoals = projectGoals.trim().length > 0;

    // Carousel-selected source render for the active angle —
    // part of the localStorage draft key so each render keeps its own draft,
    // and the source for the refine toggle's "Current design" thumbnail.
    const selectedRenderId = useConceptStore((state) =>
      conceptAngleId ? state.ui.selectedRenderId[conceptAngleId] : undefined
    );

    // Get state and actions from store
    const state = useRenderSettingsState();
    const { formData, selectedControlIds } = state;
    // Refine without any user input would render an identical copy — surface the
    // same hard-gate signal the disabled Generate enforces. Mirrors Apple/Android.
    const emptyRefine = isEmptyRefine(formData);
    // The "describe what to change" dot is a call to action for an editable
    // form — suppress it in the read-only readout (it's already rendering).
    const showEmptyRefineDot = emptyRefine && !isReadOnly;
    const renderMode = useRenderStore((state) => state.formData.mode);
    const setRefineMode = useRenderStore((state) => state.setRefineMode);
    const actions = useRenderSettingsActions();
    const sectionChanges = useSectionChanges();

    // Source render that drives the refine toggle's right tile + parent_render_id.
    // Reuses the central render selector so the toggle stays in sync with the
    // carousel selection on both main and derivative panels. Falls back to the
    // most recent completed render in the stack when nothing is explicitly
    // selected.
    const { currentRender } = useAngleRendersData({
      conceptId,
      angleId: conceptAngleId,
      selectedRenderId,
    });
    const currentRenderImageUrl =
      currentRender?.relationships?.file?.meta?.url ??
      currentRender?.attributes?.file_url ??
      null;
    const currentRenderId = currentRender?.id ?? null;

    // Read-only refine readout: the in-flight render has no completed image of
    // its own, so point the toggle + structure backdrop at the PARENT render
    // (the image being refined). That makes the Refine pill show and the
    // backdrop match what's actually under iteration. Outside read-only these
    // pass through to the carousel-selected render unchanged.
    const effectiveRenderImageUrl = isReadOnly
      ? readOnlyParentRenderUrl
      : currentRenderImageUrl;
    const effectiveRenderId = isReadOnly
      ? readOnlyParentRenderId
      : currentRenderId;

    // Backdrop for the structure panel: parent render image while the form
    // is in Refine mode so the user sees the render they're iterating on
    // behind the selected control / editor; angle photo otherwise. Reactive
    // — flips back as soon as the user toggles the mode.
    const controlBackdropUrl =
      renderMode === 'refine'
        ? effectiveRenderImageUrl
        : angle?.attributes.file?.url ?? null;

    // Use hooks
    const {
      handleSubmit: submitRender,
      cleanup,
      handlePromptChange: derivativePromptChange,
      handleControlsChange: derivativeControlsChange,
    } = useRenderSettings({
      isDerivative,
      onSubmit,
      conceptAngleId,
      projectId,
    });

    const {
      setPrompt: handlePromptChange,
      setStyles: handleStyleChange,
      setMaterials: handleMaterialsChange,
      setOutputStyle: handleOutputStyleChange,
      setEnvironment: handleEnvironmentChangeBase,
      setControls: handleControlsChange,
    } = useRenderForm();

    const handleColorsChange = useCallback(
      (colors: string[] | null) => {
        actions.updateFormData({ colors });
      },
      [actions]
    );

    // Project reference handlers
    const handleStyleProjectReferencesChange = useCallback(
      (ids: string[] | null) => {
        actions.updateFormData({ style_project_reference_ids: ids });
      },
      [actions]
    );

    const handleMaterialProjectReferencesChange = useCallback(
      (ids: string[] | null) => {
        actions.updateFormData({ material_project_reference_ids: ids });
      },
      [actions]
    );

    const handleColorProjectReferencesChange = useCallback(
      (ids: string[] | null) => {
        actions.updateFormData({ color_project_reference_ids: ids });
      },
      [actions]
    );

    const handleOutputProjectReferencesChange = useCallback(
      (ids: string[] | null) => {
        actions.updateFormData({ output_project_reference_ids: ids });
      },
      [actions]
    );

    // Auto refs handlers
    const handleUseAutoStyleRefsChange = useCallback(
      (value: boolean) => {
        actions.updateFormData({ use_auto_style_refs: value });
      },
      [actions]
    );

    const handleUseAutoMaterialRefsChange = useCallback(
      (value: boolean) => {
        actions.updateFormData({ use_auto_material_refs: value });
      },
      [actions]
    );

    const handleUseAutoColorRefsChange = useCallback(
      (value: boolean) => {
        actions.updateFormData({ use_auto_color_refs: value });
      },
      [actions]
    );

    const handleEnvironmentChange = useCallback(
      (setting: string) => {
        handleEnvironmentChangeBase(setting as EnvironmentSetting);
      },
      [handleEnvironmentChangeBase]
    );

    const handleModelFamilyChange = useCallback(
      (family: RenderModelFamily) => {
        actions.updateFormData({ render_model_family: family });
      },
      [actions]
    );

    const handleEffortChange = useCallback(
      (effort: RenderEffort) => {
        actions.updateFormData({ render_effort: effort });
      },
      [actions]
    );

    const handleIsHiresChange = useCallback(
      (value: boolean) => {
        actions.updateFormData({ is_hires: value });
      },
      [actions]
    );

    const handleIsExtendedThinkingChange = useCallback(
      (value: boolean) => {
        actions.updateFormData({ is_extended_thinking: value });
      },
      [actions]
    );

    // Initialize store when concept/angle, current render, or defaultValues
    // change. Drafts are keyed per-render so carousel switches load a different
    // draft instead of wiping the current one.
    //
    // The concept-angle id and the active render id are passed for both main
    // and derivative panels — without them, `setRefineMode` skipped its
    // with-context branch and left the Explore source's `use_auto_*_refs: true`
    // in the form when toggling to Refine on main angle, and the Reset button
    // never cleared its draft (clearDraft/persistDraft both guard on angleId).
    // Mirrors Apple `RenderSettingsSheet.setupDraftContext`, which always
    // passes `conceptAngleId`. The render id resolves through `currentRender`
    // (carousel pick → latest completed render) so main angle, which has no
    // carousel UI, still carries the source render id needed for the active-
    // mode pointer to survive reloads.
    //
    // Default-mode rule (cross-platform parity, see Apple/Android):
    //   - Angle has at least one completed render → default = 'refine'
    //   - Angle has no renders                    → default = 'explore'
    // The user's persisted choice (per concept × angle × render context)
    // always wins — this fallback only fires on first visit.
    const currentRenderModeFallback: RenderMode = currentRender
      ? 'refine'
      : 'explore';
    // Separate from the fallback above: parentRenderMode describes the
    // SOURCE render itself (for Explore baseline blanking on a Refine
    // source — see RENDER FORM SPEC rule 1). Without this split, the
    // refine-by-default rule would also blank the Explore baseline.
    const parentRenderMode: RenderMode | undefined =
      currentRender?.attributes?.mode ?? undefined;
    useEffect(() => {
      const currentConceptId = conceptId;
      const ctxAngleId = conceptAngleId;
      const ctxRenderId = currentRenderId ?? undefined;

      if (!currentConceptId) {
        return;
      }

      // Read-only refine readout: seed the form straight from the in-flight
      // render's own settings (already mapped into `defaultValues`) and force
      // Refine mode, bypassing the draft store so a blank refine baseline can't
      // clobber it. The body is locked below; nothing is persisted. On
      // completion `isReadOnly` flips false and the normal path resumes.
      if (isReadOnly && defaultValues) {
        actions.loadReadOnlySnapshot(
          defaultValues,
          currentConceptId,
          ctxAngleId,
          ctxRenderId,
          readOnlyParentRenderId
        );
        return;
      }

      if (defaultValues && Object.keys(defaultValues).length > 0) {
        actions.initializeFormData(
          defaultValues,
          currentConceptId,
          ctxAngleId,
          ctxRenderId,
          currentRenderModeFallback,
          parentRenderMode
        );
      } else {
        actions.initializeFormData(
          {
            version: 1,
            seed: generateSeed(),
            prompts: { prompt_concept: '', prompt_angle: '' },
            controls: null,
            style_ids: null,
            material_ids: null,
            colors: null,
            output_style_id: null,
            environment_setting: EnvironmentSettingEnum.NO_CHANGE,
          },
          currentConceptId,
          ctxAngleId,
          ctxRenderId,
          currentRenderModeFallback,
          parentRenderMode
        );
      }
    }, [
      conceptId,
      conceptAngleId,
      defaultValues,
      currentRenderId,
      actions,
      currentRenderModeFallback,
      parentRenderMode,
      isReadOnly,
      readOnlyParentRenderId,
    ]);

    // Handle mode changes
    useEffect(() => {
      actions.setIsDerivative(isDerivative);
    }, [isDerivative, actions]);

    // Cleanup only on true unmount
    useEffect(() => {
      return () => {
        cleanup();
      };
    }, []);

    // Memoized handlers
    const handleRender = useCallback(async () => {
      await submitRender();
    }, [submitRender]);

    // Expose submit function through ref
    useImperativeHandle(
      ref,
      () => ({
        submitRender: handleRender,
      }),
      [handleRender]
    );

    // Recreate/Refine toggle — bare segmented control (NOT inside a
    // SettingsCard), identical for main and derivative angles.
    //
    // The toggle and its server-driven nudge callout always scroll WITH the
    // settings now — the two-column inspector no longer pins the toggle above
    // the scroll (removed 2026-06-10 so the pill could fuse with the first
    // settings card). On the main angle the pill is DOCKED into the top of the
    // first SettingsCard (~60% above the card on the page background, ~40%
    // sunken into a tall card-surface band) with the nudge ABOVE it, caret
    // pointing down. Derivative angles keep the bare in-flow pill. Mirrors
    // Android.
    // Hide the Explore/Refine toggle until the angle has a render to refine on.
    // With no render, Refine is unreachable (the segment can't be selected) so
    // the toggle is dead chrome — the only available action is the implicit
    // Explore. Same gate as RenderModeToggle's own `isRefineAvailable`; mirrors
    // Apple/Android.
    const renderModeToggleControl =
      effectiveRenderImageUrl && effectiveRenderId ? (
        <RenderModeToggle
          mode={renderMode}
          currentRenderImageUrl={effectiveRenderImageUrl}
          currentRenderId={effectiveRenderId}
          onModeChange={setRefineMode}
        />
      ) : null;

    // Server-driven nudge slot (single banner) rendered ABOVE the toggle, caret
    // on the BOTTOM edge pointing DOWN at the pill below it. The centered pill
    // is narrower than this max-w-md callout, so the caret is pinned to the
    // callout center (0.5) for every caret type — aiming at the pill, not into
    // the gutter beside it. Three tiered types share the slot:
    //  - quality_warning: deep refine chain may drift in quality → plain info
    //    NoticeBanner (no caret, not tappable); the user navigates back to an
    //    earlier design themselves. Takes priority server-side over the others.
    //  - choose_path (right after the first render): informational only (no
    //    onActivate) — it orients the user to both options.
    //  - new_variant (default, incl. legacy payloads without `type`): tapping
    //    selects Explore mode. Persists until the server recomputes after a
    //    new variant is created.
    const variantNudgeCallout = variantNudge ? (
      variantNudge.type === 'quality_warning' ? (
        <NoticeBanner
          variant="info"
          title={variantNudge.title}
          message={variantNudge.message}
        />
      ) : variantNudge.type === 'choose_path' ? (
        <VariantNudgeCallout
          nudge={variantNudge}
          caretFraction={0.5}
          caretEdge="bottom"
        />
      ) : (
        <VariantNudgeCallout
          nudge={variantNudge}
          caretFraction={0.5}
          caretEdge="bottom"
          onActivate={() => setRefineMode('explore', null)}
        />
      )
    ) : null;

    // Render-mode pill embed geometry (main angle): the h-10 (40px) pill is
    // DOCKED into the top of the first SettingsCard — 24px (pt-6) rises above
    // the card on the page background, 16px sinks into a 32px (h-8) card-surface
    // band (16px overlap + 16px breathing gap) above the first row, so the pill
    // reads as the group's header. The pill is drawn AFTER the card in the DOM
    // so it stacks on top without z-index games. Collapses entirely when the
    // toggle is hidden (no render to refine on). Mirrors Android/Apple.
    const dockedToggleCard = (cardContent: ReactNode) => (
      <div className="space-y-2">
        {renderModeToggleControl ? variantNudgeCallout : null}
        <div className="relative">
          <div className={renderModeToggleControl ? 'pt-6' : undefined}>
            <SettingsCard
              surfaceClassName={showEmptyRefineDot ? 'relative' : undefined}
            >
              {/* Orange "needs attention" dot — anchored to the card's own
                  top-right corner so it reads as the group's badge, not a
                  free-floating marker beside it. Shown ONLY while Refine has no
                  user-supplied intent (`emptyRefine`, refine-mode-only by
                  construction): it signals the same requirement the disabled
                  Generate enforces — add something to change. Non-interactive
                  (decorative + a11y label). Cross-platform parity with
                  Apple/Android. Suppressed in the read-only readout — "describe
                  what to change" is meaningless while it's already rendering. */}
              {showEmptyRefineDot && (
                <span
                  role="img"
                  aria-label={tRefine('emptyRefineHint')}
                  title={tRefine('emptyRefineHint')}
                  className="absolute top-2 right-2 z-10 w-2 h-2 rounded-full bg-warning pointer-events-none animate-attention-pulse"
                />
              )}
              {renderModeToggleControl && <div aria-hidden className="h-8" />}
              {cardContent}
            </SettingsCard>
          </div>
          {renderModeToggleControl && (
            <div className="absolute top-0 inset-x-0">
              {renderModeToggleControl}
            </div>
          )}
        </div>
      </div>
    );

    // Settings content. `bottomScrollPadding=lg` reserves room for an outer
    // floating CTA so the last card never slips under the button.
    const renderSettingsBody = () => (
      <div
        // Read-only refine readout: lock every row + the toggle (swallow clicks)
        // and dim the form. Generate / "+1" are already gated by the in-flight
        // render elsewhere. Mirrors Apple's `.disabled(isReadOnly)`.
        aria-disabled={isReadOnly || undefined}
        className={
          // Stacked (single-column phone): tight pt-2 (8px) so the settings —
          // led by the Explore/Refine toggle — sit close under the angle image
          // above, matching Apple/Android's 8pt image→toggle gap. NO horizontal
          // padding here: the concept page already supplies the page gutter
          // (px-4 sm:px-6 lg:px-8) around this stacked panel, so an inner px-4
          // doubled it and made the settings column visibly narrower than the
          // Estimate tab. Two-column lives in the inspector aside (no ancestor
          // gutter) and keeps its own px-4.
          'space-y-4 w-full overflow-hidden ' +
          (stacked ? 'pt-2 ' : 'py-4 px-4 ') +
          (bottomScrollPadding === 'lg' ? 'pb-24 ' : '') +
          (isReadOnly ? 'pointer-events-none select-none opacity-60' : '')
        }
      >
            {/* Single soft ~80% pack-usage warning (designs) — server-driven
                `low` band on the building's pack, no counters or countdowns
                (pivot rule). Top of the settings so every Generate surface
                (stacked + two-column inspector, main + derivative) shows it;
                renders nothing off the `low` band. */}
            <PackUsageNotice kind="designs" />

            {/* Main Angle Settings - iOS-style grouped sections */}
            {!isDerivative && (
              <>
                {/* Server-driven "save as reference" steer nudge — one-time info
                    banner at the very top of the main-angle settings, shown at
                    the "Design #N" milestones. Independent of the render-mode
                    nudge slot below; text is localized server-side. */}
                {referenceNudge && (
                  <NoticeBanner
                    variant="info"
                    title={referenceNudge.title}
                    message={referenceNudge.message}
                  />
                )}

                {/* CREATIVE INPUTS Card — ONE group: brief (Project goals in
                    Explore + inline prompt editor + Sketches), style/materials/
                    colors references, and environment. Everything that
                    describes WHAT to render, bundled so it reads as one block
                    distinct from the engine/output controls below. Goals drives
                    the situation/intent the prompt builds on. The Explore/Refine
                    pill is DOCKED into this card's top via dockedToggleCard,
                    with the variant nudge above it. Mirrors Apple/Android. */}
                {dockedToggleCard(
                  <SettingsRowGroup>
                  {renderMode === 'explore' && projectId && (
                    <ProjectGoalsSection
                      projectId={projectId}
                      open={goalsSheetOpen}
                      onOpenChange={setGoalsSheetOpen}
                    />
                  )}
                  <PromptsSection
                    conceptPrompt={formData.prompts?.prompt_concept || ''}
                    anglePrompt={formData.prompts?.prompt_angle || ''}
                    onConceptPromptChange={(value) =>
                      handlePromptChange('concept', value)
                    }
                    onAnglePromptChange={(value) =>
                      handlePromptChange('angle', value)
                    }
                    hasChanges={
                      'prompts' in sectionChanges
                        ? sectionChanges.prompts
                        : false
                    }
                    isRefine={renderMode === 'refine'}
                    goalsRequired={!!projectId && !hasProjectGoals}
                    projectId={projectId}
                    onOpenGoals={() => setGoalsSheetOpen(true)}
                  />
                  <StructureSection
                    controls={controls}
                    selectedControlIds={selectedControlIds}
                    angle={angle}
                    backdropUrl={controlBackdropUrl}
                    onControlsChange={handleControlsChange}
                    onControlsUpdate={onControlsUpdate}
                    hasChanges={
                      'structure' in sectionChanges
                        ? sectionChanges.structure
                        : false
                    }
                  />
                  {/* Style / Materials / Colors references — same group,
                      below the brief so users see Prompts/Structure first. */}
                  <StyleSection
                    selectedStyleIds={formData.style_ids}
                    onStyleChange={handleStyleChange}
                    hasChanges={
                      'style' in sectionChanges ? sectionChanges.style : false
                    }
                    projectId={projectId}
                    styleProjectReferenceIds={
                      formData.style_project_reference_ids
                    }
                    onStyleProjectReferencesChange={
                      handleStyleProjectReferencesChange
                    }
                    useAutoStyleRefs={formData.use_auto_style_refs}
                    onUseAutoStyleRefsChange={handleUseAutoStyleRefsChange}
                    onNavigateToReferences={onNavigateToReferences}
                    isRefine={renderMode === 'refine'}
                  />
                  <MaterialsSection
                    selectedMaterialIds={formData.material_ids}
                    onMaterialsChange={handleMaterialsChange}
                    hasChanges={
                      'materials' in sectionChanges
                        ? sectionChanges.materials
                        : false
                    }
                    projectId={projectId}
                    materialProjectReferenceIds={
                      formData.material_project_reference_ids
                    }
                    onMaterialProjectReferencesChange={
                      handleMaterialProjectReferencesChange
                    }
                    useAutoMaterialRefs={formData.use_auto_material_refs}
                    onUseAutoMaterialRefsChange={
                      handleUseAutoMaterialRefsChange
                    }
                    onNavigateToReferences={onNavigateToReferences}
                    isRefine={renderMode === 'refine'}
                  />
                  <ColorsSection
                    colors={formData.colors}
                    onColorsChange={handleColorsChange}
                    hasChanges={
                      'colors' in sectionChanges ? sectionChanges.colors : false
                    }
                    projectId={projectId}
                    colorProjectReferenceIds={
                      formData.color_project_reference_ids
                    }
                    onColorProjectReferencesChange={
                      handleColorProjectReferencesChange
                    }
                    useAutoColorRefs={formData.use_auto_color_refs}
                    onUseAutoColorRefsChange={handleUseAutoColorRefsChange}
                    onNavigateToReferences={onNavigateToReferences}
                    isRefine={renderMode === 'refine'}
                  />
                  {/* Environment — last of the creative inputs. */}
                  <EnvironmentSection
                    environmentSetting={formData.environment_setting}
                    onEnvironmentChange={handleEnvironmentChange}
                    hasChanges={
                      'environment' in sectionChanges
                        ? sectionChanges.environment
                        : false
                    }
                  />
                  </SettingsRowGroup>
                )}

                {/* ENGINE & OUTPUT Card — the SECOND (and last) group. Render
                    settings carry exactly two groups: the creative inputs above
                    (WHAT to render) and this engine/output block (HOW it's
                    rendered). Output style + Model are universal base features for
                    EVERYONE (Free, Pack, Builder) since the Facade Pack pivot
                    (model_selection, like output_style). The manual engine knobs
                    (Effort / Extended thinking / Hi-Res) append to the SAME card
                    only for advanced-control holders (Builder Pass, grandfathered
                    Pro/Designer); Free + one-time Facade Pack buyers never see them
                    — the server auto-resolves the engine and the render request
                    omits the matching keys. The doubled top gap (the container's
                    space-y-4 margin + this card's pt-4 padding) sets the group
                    apart from the creative inputs above. Mirrors Apple/Android. */}
                <SettingsCard className="pt-4">
                  <SettingsRowGroup>
                  <ModelSection
                    selectedModelFamily={formData.render_model_family || 'AURORA'}
                    onModelFamilyChange={handleModelFamilyChange}
                    hasChanges={
                      'model' in sectionChanges
                        ? Boolean(sectionChanges.model)
                        : false
                    }
                  />
                  <OutputStyleCarousel
                    selectedOutputStyleId={formData.output_style_id}
                    onOutputStyleChange={handleOutputStyleChange}
                    hasChanges={
                      'output' in sectionChanges ? sectionChanges.output : false
                    }
                    projectId={projectId}
                    outputProjectReferenceIds={
                      formData.output_project_reference_ids
                    }
                    onOutputProjectReferencesChange={
                      handleOutputProjectReferencesChange
                    }
                    onNavigateToReferences={onNavigateToReferences}
                  />
                  {hasAdvancedRenderControls && (
                    <>
                      <EffortSection
                        selectedEffort={formData.render_effort || 'STANDARD'}
                        onEffortChange={handleEffortChange}
                      />
                      <ExtendedThinkingSection
                        isExtendedThinking={formData.is_extended_thinking}
                        onIsExtendedThinkingChange={handleIsExtendedThinkingChange}
                      />
                      <HiResSection
                        isHires={formData.is_hires}
                        onIsHiresChange={handleIsHiresChange}
                      />
                    </>
                  )}
                  </SettingsRowGroup>
                </SettingsCard>
              </>
            )}

            {/* Derivative Angle Settings - iOS-style grouped */}
            {isDerivative && (
              <>
                {/* CREATIVE INPUTS Card — ONE group: brief (inline angle-prompt
                    editor + Sketches) and environment. Derivatives inherit
                    project goals and the style/materials/colors references from
                    the main angle, so only the brief + environment show here.
                    The Explore/Refine pill is DOCKED into this card's top via
                    dockedToggleCard, same as the main angle; the inherited-
                    settings note renders as plain secondary text inside the
                    card, above the brief (replaced the old standalone info
                    banner 2026-06-10). Mirrors Apple/Android. */}
                {dockedToggleCard(
                  <SettingsRowGroup>
                  {/* Inherited-from-main note — Explore only: Refine targets
                      the selected derivative render directly and inherits
                      nothing from the main view. */}
                  {renderMode === 'explore' && (
                    <p
                      className={
                        'text-list-item-subtitle text-muted-foreground px-4 ' +
                        (renderModeToggleControl ? '' : 'pt-3.5')
                      }
                    >
                      {tInherited('description')}
                    </p>
                  )}
                  <PromptsSection
                    conceptPrompt=""
                    anglePrompt={formData.prompts?.prompt_angle || ''}
                    onConceptPromptChange={() => {}}
                    onAnglePromptChange={(value) =>
                      derivativePromptChange('angle', value)
                    }
                    hasChanges={
                      'prompts' in sectionChanges
                        ? sectionChanges.prompts
                        : false
                    }
                    isDerivative
                    isRefine={renderMode === 'refine'}
                  />
                  <StructureSection
                    controls={controls}
                    selectedControlIds={selectedControlIds}
                    angle={angle}
                    backdropUrl={controlBackdropUrl}
                    onControlsChange={derivativeControlsChange}
                    onControlsUpdate={onControlsUpdate}
                    hasChanges={
                      'structure' in sectionChanges
                        ? sectionChanges.structure
                        : false
                    }
                  />
                  {/* Environment — last of the creative inputs. Defaults inherit
                      from the main angle's render via use-render-panel-logic
                      defaultValues; the user can still override per derivative. */}
                  <EnvironmentSection
                    environmentSetting={formData.environment_setting}
                    onEnvironmentChange={handleEnvironmentChange}
                    hasChanges={
                      'environment' in sectionChanges
                        ? sectionChanges.environment
                        : false
                    }
                  />
                  </SettingsRowGroup>
                )}

                {/* ENGINE & OUTPUT Card — the SECOND (and last) group. Render
                    settings carry exactly two groups: the creative inputs above
                    (WHAT to render) and this engine/output block (HOW it's
                    rendered). Output style + Model are universal base features for
                    EVERYONE (Free, Pack, Builder) since the Facade Pack pivot
                    (model_selection, like output_style). The manual engine knobs
                    (Effort / Extended thinking / Hi-Res) append to the SAME card
                    only for advanced-control holders (Builder Pass, grandfathered
                    Pro/Designer); Free + one-time Facade Pack buyers never see them
                    — the server auto-resolves the engine and the render request
                    omits the matching keys. The doubled top gap (the container's
                    space-y-4 margin + this card's pt-4 padding) sets the group
                    apart from the creative inputs above. Mirrors Apple/Android. */}
                <SettingsCard className="pt-4">
                  <SettingsRowGroup>
                  <ModelSection
                    selectedModelFamily={formData.render_model_family || 'AURORA'}
                    onModelFamilyChange={handleModelFamilyChange}
                    hasChanges={
                      'model' in sectionChanges
                        ? Boolean(sectionChanges.model)
                        : false
                    }
                  />
                  <OutputStyleCarousel
                    selectedOutputStyleId={formData.output_style_id}
                    onOutputStyleChange={handleOutputStyleChange}
                    hasChanges={
                      'output' in sectionChanges ? sectionChanges.output : false
                    }
                    projectId={projectId}
                    outputProjectReferenceIds={
                      formData.output_project_reference_ids
                    }
                    onOutputProjectReferencesChange={
                      handleOutputProjectReferencesChange
                    }
                    onNavigateToReferences={onNavigateToReferences}
                  />
                  {hasAdvancedRenderControls && (
                    <>
                      <EffortSection
                        selectedEffort={formData.render_effort || 'STANDARD'}
                        onEffortChange={handleEffortChange}
                      />
                      <ExtendedThinkingSection
                        isExtendedThinking={formData.is_extended_thinking}
                        onIsExtendedThinkingChange={handleIsExtendedThinkingChange}
                      />
                      <HiResSection
                        isHires={formData.is_hires}
                        onIsHiresChange={handleIsHiresChange}
                      />
                    </>
                  )}
                  </SettingsRowGroup>
                </SettingsCard>
              </>
            )}
      </div>
    );

    // Stacked (single-column): render in normal page flow so the angle image
    // above and these settings scroll together as ONE page scroll, with the
    // Generate button floating above the bottom nav (owned by the caller).
    if (stacked) {
      return (
        <div className="w-full overflow-x-hidden">{renderSettingsBody()}</div>
      );
    }

    // Two-column: the Explore/Refine toggle is NOT pinned above the scroll
    // anymore — it scrolls with the settings, docked into the first card (see
    // dockedToggleCard). The pinned fixed-header variant was removed
    // deliberately (2026-06-10) so the pill could fuse with the group on all
    // layouts.
    return (
      <div className="flex flex-col h-full min-h-0 overflow-x-hidden">
        <ScrollArea className="flex-1 min-h-0 w-full">
          {renderSettingsBody()}
        </ScrollArea>
      </div>
    );
  }
);

ScrollableRenderSettings.displayName = 'ScrollableRenderSettings';
